import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { mathToHtml } from './MathText';

// Builds the generated paper as a PDF in the browser.
//
// The paper is laid out as HTML off-screen (so KaTeX renders the math exactly as
// it does on the cards), then each block - the header, a section heading, one
// question - is captured with html2canvas and placed on A4 pages. Capturing per
// block lets page breaks fall between questions instead of through them; a block
// taller than a page is sliced across pages.
//
// The captures are pictures, so each block's words are also written over its
// image as invisible text, placed where the browser laid them out. That is what
// lets a teacher select, copy and search the downloaded paper.
//
// Input is a plain export model built by the generation screen (see
// TestGenerationTab's buildExportModel), so this file knows nothing about the
// generator's response shapes.

const PAGE_W_MM = 210;
const PAGE_H_MM = 297;
const MARGIN_MM = 12;
const FOOTER_MM = 8;
const BLOCK_GAP_MM = 3;
const RENDER_WIDTH_PX = 720;
const IMAGE_TIMEOUT_MS = 8000;
const PT_PER_MM = 72 / 25.4;
// A Unicode font for the invisible text layer; jsPDF's built-in fonts cannot
// carry symbols such as − or Greek letters. Served from public/, fetched on the
// first download only.
const TEXT_FONT_URL = '/test-assets/fonts/DejaVuSans.ttf';
const TEXT_FONT = 'PaperText';
const LATIN1 = /^[\x20-\x7E -ÿ]*$/;

const STYLES = `
  .pp-root { font-family: Roboto, Arial, sans-serif; color: #1a1a1a; font-size: 14px; line-height: 1.55; }
  .pp-root * { box-sizing: border-box; }
  .pp-header { text-align: center; padding-bottom: 10px; border-bottom: 2px solid #594099; }
  .pp-title { font-size: 22px; font-weight: 700; margin: 0 0 4px; }
  .pp-meta { font-size: 13px; color: #555; }
  .pp-kind { display: inline-block; margin-top: 6px; font-size: 12px; font-weight: 600; color: #594099; }
  .pp-section { padding: 6px 0 4px; border-bottom: 1px solid #ddd; display: flex; justify-content: space-between; align-items: baseline; gap: 12px; }
  .pp-section-title { font-size: 16px; font-weight: 700; color: #594099; }
  .pp-section-sub { font-size: 12px; color: #555; white-space: nowrap; }
  .pp-q { padding: 8px 0 4px; }
  .pp-q-head { display: flex; gap: 8px; align-items: flex-start; }
  .pp-q-num { font-weight: 700; flex-shrink: 0; min-width: 34px; }
  .pp-q-text { flex: 1; min-width: 0; overflow-wrap: anywhere; }
  .pp-q-marks { flex-shrink: 0; font-size: 12px; color: #555; white-space: nowrap; padding-top: 2px; }
  .pp-indent { margin-left: 42px; }
  .pp-tags { font-size: 11px; color: #777; margin: 2px 0 0 42px; }
  .pp-diagram { margin: 8px 0 4px 42px; }
  .pp-diagram img { max-width: 420px; max-height: 300px; }
  .pp-opts { margin: 6px 0 0 42px; display: flex; flex-direction: column; gap: 4px; }
  .pp-opt { display: flex; gap: 8px; padding: 2px 6px; border-radius: 4px; }
  .pp-opt.correct { background: #e8f5e9; font-weight: 600; }
  .pp-opt-letter { font-weight: 600; flex-shrink: 0; }
  .pp-matrix { margin: 8px 0 0 42px; width: calc(100% - 42px); border-collapse: collapse; font-size: 13px; }
  .pp-matrix th, .pp-matrix td { border: 1px solid #ccc; padding: 4px 8px; vertical-align: top; text-align: left; }
  .pp-matrix th { background: #f4f0ff; }
  .pp-subs { margin: 6px 0 0 42px; padding: 0; list-style: none; display: flex; flex-direction: column; gap: 4px; }
  .pp-sub { display: flex; gap: 8px; }
  .pp-sub-num { font-weight: 600; flex-shrink: 0; min-width: 32px; }
  .pp-sub-text { flex: 1; min-width: 0; }
  .pp-sub-marks { flex-shrink: 0; font-size: 12px; color: #555; }
  .pp-sol { margin: 8px 0 0 42px; padding: 8px 10px; background: #f7f5fc; border-left: 3px solid #594099; border-radius: 4px; font-size: 13px; }
  .pp-sol-label { font-weight: 700; color: #594099; }
  .pp-sol-block { margin-top: 6px; }
  .pp-points { margin: 4px 0 0; padding-left: 18px; }
  .pp-points li { margin: 2px 0; }
  .pp-multiline { white-space: normal; }
`;

const html = (value) => mathToHtml(value);

const formatMarks = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return String(value ?? '');
  // Three places: Banking Mains marks go down to -0.375 and -0.125.
  return Number.isInteger(n) ? String(n) : String(Number(n.toFixed(3)));
};

const marksLabel = (q) => {
  if (q.marks === null || q.marks === undefined || q.marks === '') return '';
  const pos = `${formatMarks(q.marks)} ${Number(q.marks) === 1 ? 'mark' : 'marks'}`;
  const neg = Number(q.negativeMarks);
  return Number.isFinite(neg) && neg !== 0 ? `[${pos}, −${formatMarks(Math.abs(neg))}]` : `[${pos}]`;
};

const headerBlock = (model, withSolutions, count) => {
  const meta = [
    model.exam,
    model.subject,
    `${count} question${count === 1 ? '' : 's'}`,
    model.totalMarks !== null && model.totalMarks !== undefined ? `Maximum marks: ${formatMarks(model.totalMarks)}` : null,
    model.duration ? `Duration: ${model.duration} min` : null
  ].filter(Boolean);
  return `
    <div class="pp-header">
      <div class="pp-title">${html(model.title || 'Test Paper')}</div>
      <div class="pp-meta">${meta.map(html).join(' &nbsp;·&nbsp; ')}</div>
      <div class="pp-kind">${withSolutions ? 'With solutions' : 'Question paper'}</div>
    </div>`;
};

const sectionBlock = (group) => `
  <div class="pp-section">
    <span class="pp-section-title">${html(group.heading)}</span>
    ${group.subheading ? `<span class="pp-section-sub">${html(group.subheading)}</span>` : ''}
  </div>`;

const solutionBlock = (q) => {
  const parts = [];
  const a = q.answer || {};
  if (a.kind === 'written') {
    parts.push(`<div><span class="pp-sol-label">Model answer:</span> ${html(a.modelAnswer || 'N/A')}</div>`);
    if (Array.isArray(a.markingPoints) && a.markingPoints.length > 0) {
      parts.push(`
        <div class="pp-sol-block">
          <span class="pp-sol-label">Marking points:</span>
          <ul class="pp-points">
            ${a.markingPoints.map((mp) => `<li>${html(mp.point)} · <b>${formatMarks(mp.marks)}</b></li>`).join('')}
          </ul>
        </div>`);
    }
  } else if (a.kind === 'case') {
    const subs = Array.isArray(q.subQuestions) ? q.subQuestions : [];
    if (subs.length > 0) {
      parts.push(`<div class="pp-sol-label">Answers:</div>`);
      parts.push(`<ul class="pp-subs" style="margin-left:0">${subs.map((s) => `
        <li class="pp-sub"><span class="pp-sub-num">(${s.numeral})</span><span class="pp-sub-text">${html(s.answer)}</span></li>`).join('')}</ul>`);
    } else {
      parts.push(`<div><span class="pp-sol-label">Answer:</span> ${html(a.text || 'N/A')}</div>`);
    }
  } else {
    parts.push(`<div><span class="pp-sol-label">Answer:</span> ${html(a.text || 'N/A')}</div>`);
  }
  if (q.explanation) {
    parts.push(`<div class="pp-sol-block"><span class="pp-sol-label">Solution:</span> ${html(q.explanation)}</div>`);
  }
  return `<div class="pp-sol">${parts.join('')}</div>`;
};

const questionBlock = (q, withSolutions) => {
  const tags = [q.subject, q.chapter, q.typeLabel].filter(Boolean);
  const matrix = q.matrix && (q.matrix.colA.length > 0 || q.matrix.colB.length > 0)
    ? (() => {
      const rows = Math.max(q.matrix.colA.length, q.matrix.colB.length);
      const cell = (item) => (item ? `<b>${html(item.label)}</b> ${html(item.text)}` : '');
      return `
        <table class="pp-matrix">
          <thead><tr><th>List I</th><th>List II</th></tr></thead>
          <tbody>${Array.from({ length: rows }).map((_, i) => `
            <tr><td>${cell(q.matrix.colA[i])}</td><td>${cell(q.matrix.colB[i])}</td></tr>`).join('')}
          </tbody>
        </table>`;
    })()
    : '';
  const options = Array.isArray(q.options) && q.options.length > 0
    ? `<div class="pp-opts">${q.options.map((opt) => `
        <div class="pp-opt ${withSolutions && opt.correct ? 'correct' : ''}">
          <span class="pp-opt-letter">(${opt.letter})</span><span>${html(opt.text)}</span>
        </div>`).join('')}</div>`
    : '';
  const subs = Array.isArray(q.subQuestions) && q.subQuestions.length > 0
    ? `<ul class="pp-subs">${q.subQuestions.map((s) => `
        <li class="pp-sub">
          <span class="pp-sub-num">(${s.numeral})</span>
          <span class="pp-sub-text">${html(s.text)}</span>
          ${s.marks !== null && s.marks !== undefined ? `<span class="pp-sub-marks">[${formatMarks(s.marks)}]</span>` : ''}
        </li>`).join('')}</ul>`
    : '';
  return `
    <div class="pp-q">
      <div class="pp-q-head">
        <span class="pp-q-num">Q${q.number}.</span>
        <div class="pp-q-text">${html(q.text)}</div>
        <span class="pp-q-marks">${html(marksLabel(q))}</span>
      </div>
      ${tags.length > 0 ? `<div class="pp-tags">${tags.map(html).join(' · ')}</div>` : ''}
      ${q.diagramUrl ? `<div class="pp-diagram"><img src="${encodeURI(q.diagramUrl)}" crossorigin="anonymous" alt="" /></div>` : ''}
      ${matrix}
      ${options}
      ${subs}
      ${withSolutions ? solutionBlock(q) : ''}
    </div>`;
};

const waitForImages = (root) => {
  const images = Array.from(root.querySelectorAll('img'));
  return Promise.all(images.map((img) => new Promise((resolve) => {
    if (img.complete) {
      resolve();
      return;
    }
    const done = () => resolve();
    img.addEventListener('load', done, { once: true });
    img.addEventListener('error', () => {
      // An unreachable diagram must not fail the whole export.
      img.remove();
      resolve();
    }, { once: true });
    setTimeout(done, IMAGE_TIMEOUT_MS);
  })));
};

// Resolves to the font as a binary string, or null if it cannot be fetched (the
// paper then gets a text layer for plain Latin text only). A failure is retried
// on the next download.
let textFontPromise = null;
const loadTextFont = () => {
  if (!textFontPromise) {
    textFontPromise = fetch(TEXT_FONT_URL)
      .then((res) => (res.ok ? res.arrayBuffer() : Promise.reject(new Error(`HTTP ${res.status}`))))
      .then((buffer) => {
        const bytes = new Uint8Array(buffer);
        let binary = '';
        for (let i = 0; i < bytes.length; i += 0x8000) {
          binary += String.fromCharCode.apply(null, bytes.subarray(i, i + 0x8000));
        }
        return binary;
      })
      .catch(() => {
        textFontPromise = null;
        return null;
      });
  }
  return textFontPromise;
};

// The block's words grouped into runs that sit on one line, with each run's box
// in CSS px relative to the block. KaTeX's hidden MathML copy is skipped so math
// is only picked up once, from the glyphs actually drawn.
const collectTextRuns = (block) => {
  const origin = block.getBoundingClientRect();
  const walker = document.createTreeWalker(block, NodeFilter.SHOW_TEXT, {
    acceptNode: (node) => (node.parentElement?.closest('.katex-mathml') ? NodeFilter.FILTER_REJECT : NodeFilter.FILTER_ACCEPT)
  });
  const range = document.createRange();
  const runs = [];
  for (let node = walker.nextNode(); node; node = walker.nextNode()) {
    const fontPx = parseFloat(getComputedStyle(node.parentElement).fontSize) || 14;
    const words = /\S+/g;
    let run = null;
    let match;
    while ((match = words.exec(node.data))) {
      range.setStart(node, match.index);
      range.setEnd(node, match.index + match[0].length);
      const r = range.getBoundingClientRect();
      if (!r.width || !r.height) continue;
      if (run && Math.abs(r.top - run.top) < r.height / 2 && r.left >= run.right - 1) {
        run.text += ` ${match[0]}`;
        run.right = r.right;
        run.bottom = Math.max(run.bottom, r.bottom);
      } else {
        if (run) runs.push(run);
        run = { text: match[0], left: r.left, right: r.right, top: r.top, bottom: r.bottom, fontPx };
      }
    }
    if (run) runs.push(run);
  }
  return runs.map((run) => ({
    ...run,
    left: run.left - origin.left,
    right: run.right - origin.left,
    top: run.top - origin.top,
    bottom: run.bottom - origin.top
  }));
};

// Writes the runs whose middle falls in [fromPx, toPx) of the block as invisible
// text, stretched to the width the browser gave them so a selection lines up
// with the picture underneath. `mmPerCssPx` maps block px to page mm, and
// (x, y) is where block px `fromPx` lands on the current page.
const writeTextRuns = (doc, runs, { font, mmPerCssPx, x, y, fromPx = 0, toPx = Infinity }) => {
  doc.setFont(font || 'helvetica', 'normal');
  runs.forEach((run) => {
    const mid = (run.top + run.bottom) / 2;
    if (mid < fromPx || mid >= toPx) return;
    if (!font && !LATIN1.test(run.text)) return;
    try {
      doc.setFontSize(run.fontPx * mmPerCssPx * PT_PER_MM);
      const natural = doc.getTextWidth(run.text);
      if (!natural) return;
      doc.text(run.text, x + run.left * mmPerCssPx, y + (mid - fromPx) * mmPerCssPx, {
        baseline: 'middle',
        renderingMode: 'invisible',
        horizontalScale: ((run.right - run.left) * mmPerCssPx) / natural
      });
    } catch (e) {
      // A run the font cannot encode only loses its copyable text, never the export.
    }
  });
};

// Capturing blocks keeps the main thread busy for seconds; handing a frame back
// to the browser every so often keeps the download button's spinner turning.
const YIELD_EVERY_MS = 50;
const nextFrame = () => new Promise((resolve) => requestAnimationFrame(() => setTimeout(resolve, 0)));

const safeFileName = (name) =>
  String(name || 'Test Paper').replace(/[\\/:*?"<>|]+/g, ' ').replace(/\s+/g, ' ').trim().slice(0, 80) || 'Test Paper';

/**
 * @param {object}  model                 { title, exam, subject, totalMarks, duration, groups: [{heading, subheading, questions}] }
 * @param {boolean} withSolutions         include answers, marking points and explanations
 */
export const downloadPaperPdf = async (model, { withSolutions }) => {
  const groups = (model.groups || []).filter((g) => Array.isArray(g.questions) && g.questions.length > 0);
  const count = groups.reduce((sum, g) => sum + g.questions.length, 0);
  if (count === 0) throw new Error('No questions to download');

  const host = document.createElement('div');
  host.setAttribute('aria-hidden', 'true');
  host.style.cssText = `position:fixed;left:-10000px;top:0;width:${RENDER_WIDTH_PX}px;background:#fff;z-index:-1;`;
  const blocksHtml = [
    headerBlock(model, withSolutions, count),
    ...groups.flatMap((g) => [
      ...(g.heading ? [sectionBlock(g)] : []),
      ...g.questions.map((q) => questionBlock(q, withSolutions))
    ])
  ];
  host.innerHTML = `<style>${STYLES}</style><div class="pp-root">${blocksHtml
    .map((b) => `<div class="pp-block">${b}</div>`)
    .join('')}</div>`;
  document.body.appendChild(host);

  try {
    const fontLoad = loadTextFont();
    await waitForImages(host);
    if (document.fonts && document.fonts.ready) {
      await document.fonts.ready;
    }

    const doc = new jsPDF({ unit: 'mm', format: 'a4', orientation: 'portrait' });
    const fontBinary = await fontLoad;
    let textFont = null;
    if (fontBinary) {
      try {
        doc.addFileToVFS(`${TEXT_FONT}.ttf`, fontBinary);
        doc.addFont(`${TEXT_FONT}.ttf`, TEXT_FONT, 'normal');
        textFont = TEXT_FONT;
      } catch (e) {
        textFont = null;
      }
      // Parsing the font is a long synchronous step; let the spinner catch up.
      await nextFrame();
    }
    const contentW = PAGE_W_MM - MARGIN_MM * 2;
    const pageBottom = PAGE_H_MM - MARGIN_MM - FOOTER_MM;
    const contentH = pageBottom - MARGIN_MM;
    let y = MARGIN_MM;

    const blocks = Array.from(host.querySelectorAll('.pp-block'));
    let lastYield = performance.now();
    for (const block of blocks) {
      if (performance.now() - lastYield > YIELD_EVERY_MS) {
        // eslint-disable-next-line no-await-in-loop
        await nextFrame();
        lastYield = performance.now();
      }
      const runs = collectTextRuns(block);
      const blockCssW = block.getBoundingClientRect().width;
      // eslint-disable-next-line no-await-in-loop
      const canvas = await html2canvas(block, {
        scale: 2,
        useCORS: true,
        backgroundColor: '#ffffff',
        logging: false,
        // html2canvas clones the whole document for every capture. Cloning only
        // the path to this block (plus stylesheets) keeps each capture cheap on
        // a page full of question cards.
        ignoreElements: (el) =>
          document.body.contains(el) &&
          el.tagName !== 'STYLE' &&
          el.tagName !== 'LINK' &&
          !el.contains(block) &&
          !block.contains(el)
      });
      if (!canvas.width || !canvas.height) continue;
      const mmPerPx = contentW / canvas.width;
      const blockH = canvas.height * mmPerPx;
      const canvasPxPerCssPx = blockCssW ? canvas.width / blockCssW : 2;
      const mmPerCssPx = mmPerPx * canvasPxPerCssPx;

      if (blockH <= contentH) {
        if (y + blockH > pageBottom && y > MARGIN_MM) {
          doc.addPage();
          y = MARGIN_MM;
        }
        doc.addImage(canvas.toDataURL('image/jpeg', 0.92), 'JPEG', MARGIN_MM, y, contentW, blockH);
        writeTextRuns(doc, runs, { font: textFont, mmPerCssPx, x: MARGIN_MM, y });
        y += blockH + BLOCK_GAP_MM;
        continue;
      }

      // Taller than a page: slice it into page-height strips.
      if (y > MARGIN_MM) {
        doc.addPage();
        y = MARGIN_MM;
      }
      const slicePx = Math.floor(contentH / mmPerPx);
      for (let offset = 0; offset < canvas.height; offset += slicePx) {
        const h = Math.min(slicePx, canvas.height - offset);
        const slice = document.createElement('canvas');
        slice.width = canvas.width;
        slice.height = h;
        slice.getContext('2d').drawImage(canvas, 0, offset, canvas.width, h, 0, 0, canvas.width, h);
        if (offset > 0) {
          doc.addPage();
          y = MARGIN_MM;
        }
        doc.addImage(slice.toDataURL('image/jpeg', 0.92), 'JPEG', MARGIN_MM, y, contentW, h * mmPerPx);
        writeTextRuns(doc, runs, {
          font: textFont,
          mmPerCssPx,
          x: MARGIN_MM,
          y,
          fromPx: offset / canvasPxPerCssPx,
          toPx: (offset + h) / canvasPxPerCssPx
        });
        y += h * mmPerPx + BLOCK_GAP_MM;
      }
    }

    const pages = doc.getNumberOfPages();
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(120);
    for (let i = 1; i <= pages; i += 1) {
      doc.setPage(i);
      doc.text(`Page ${i} of ${pages}`, PAGE_W_MM / 2, PAGE_H_MM - MARGIN_MM / 2, { align: 'center' });
    }

    const suffix = withSolutions ? 'with solutions' : 'question paper';
    doc.save(`${safeFileName(model.title)} - ${suffix}.pdf`);
  } finally {
    host.remove();
  }
};
